import { tools } from '@/utils/tools';
import { setService, tableService } from '@/api';
import { objectRoleEnum, computingStatus } from '@/utils/const';
import { h, ref } from 'vue';
import fieldPermission from '@/hooks/field-permission';
const { isRequiredField, updateFieldPermission } = fieldPermission();

import FhFieldShow from '@/components/fh-set-fields/field-list-main/index.vue';

// const { t } = useI18n();

/*
@param entity 实体ID
@param tableLevel 表格级别
@param customType 表格类型 30 / 35
@return 返回表格配置
*/

const useLyTableConfig = (entity, tableLevel, customType) => {
  //获取多层级对应id
  const getTableLevelEntity = (modules) => {
    return modules?.businessData || '';
  };
  //获取多层级对应entity
  const getObjEntity = (multiTable, isExpand) => {
    return isExpand ? multiTable?.businessData : entity.value || multiTable?.businessData || '';
  };
  const ALIGN_RIGHT_TYPES = new Set([7, 16, 23]); // 使用Set提高查找效率

  const getColumnAlign = (fieldType, fieldExt) => {
    if (ALIGN_RIGHT_TYPES.has(fieldType)) return 'right';
    if (fieldType !== 24) return 'left';
    if (fieldType !== 24) return 'left';
    // 只有当类型是24时才尝试解析JSON
    if (typeof fieldExt !== 'string') return 'left';
    try {
      const { type } = JSON.parse(fieldExt) || {};
      return type === 0 ? 'left' : 'right';
    } catch {
      return 'left';
    }
  };

  // 获取主属性字段
  const getDetailKey = (modules) => {
    return modules?.find((pc) => pc?.setting?.mainField)?.setting?.fieldKey || 'name';
  };

  const getOperationWidth = (modules, businessData) => {
    const operationList = modules
      ?.filter((v) => v.type == 40 && v?.value != (businessData < 200 ? objectRoleEnum[businessData] + ':add' : businessData + ':add'))
      .map((n) => {
        return n.value;
      });
    return operationList?.length === 0 ? 0 : operationList?.length <= 3 ? 120 : 180;
  };

  // 创建操作列配置
  const createOperationColumn = (modules, businessData) => ({
    colType: false,
    colKey: 'operations',
    slotName: 'operations',
    title: '操作',
    stopPropagation: true,
    fixed: 'right',
    width: getOperationWidth(modules, businessData)
  });

  // 创建列配置
  const createColumnConfig = (item, businessData, tableList, subformRuleFields, subformFieldKey) => {
    const fieldKey = item?.setting?.fieldKey; //字段标识
    const frozen = item?.businessData === '1' && customType != 35; //是否为固定行
    const fieldType = item?.setting?.fieldType == 21 ? item?.setting?.business?.fieldType : item?.setting?.fieldType; //字段类型
    const fieldExt = item?.setting?.fieldType == 21 ? item?.setting?.business?.fieldExt : item?.setting?.fieldExt;
    const fieldName = item?.setting?.fieldName; //字段名称
    const mainField = item?.setting?.mainField || false; // 主属性字段

    const oldFieldType = item.fieldType;

    const defaultWidth = [8, 9].includes(fieldType) ? 160 : tools.getStrLen(item.fieldName) <= 6 ? 120 : tools.getStrLen(item.fieldName) > 10 ? 220 : tools.getStrLen(item.fieldName) * 20;

    // 表格的className属性设置单元格的类名
    const colType = {
      colKey: fieldKey,
      title: fieldName,
      fieldName: fieldName,
      fieldType: fieldType,
      oldFieldType: oldFieldType,
      fieldExt: fieldExt,
      fieldSort: item.fieldSort,
      width: mainField || [12, 25].includes(fieldType) ? 250 : defaultWidth,
      resize: tools.getStrLen(fieldName) <= 5 ? { minWidth: 120 } : tools.getStrLen(fieldName) > 10 ? { minWidth: 300 } : { minWidth: tools.getStrLen(fieldName) * 30 },
      fixed: frozen ? 'left' : undefined,
      align: getColumnAlign(fieldType, fieldExt),
      iconMore: false,
      colType: item.type === 1 ? '基础字段' : '其他',
      isSubForm: false,
      aggregationType: item?.aggregationType,
      render: (row) => {
        const params = {
          col: { ...item },
          row,
          subEntityId: item.entityId, // 子表单entity
          fieldKey: subformFieldKey ? `${subformFieldKey}.${fieldKey}` : fieldKey, // 子表单场景需要拼接字段，用于业务规则
          fieldValue: row[fieldKey],
          fieldType: fieldType,
          fieldExt: fieldExt,
          entity: businessData,
          align: getColumnAlign(fieldType, fieldExt),
          isRequired: isRequiredField(item?.fieldKey, row?.PERMISSION, item.isRequired),
          mainField: mainField,
          sourceType: 1, //1:列表 2：详情 3：关联对象表格
          isSubForm: false,
          isAutoHeight: true,
          tableLevel: tableLevel,
          isAssTable: true, // 是否是查找关联表格
          permissions: row.LOGIC_ROLE_PERMISSION,
          PERMISSION: subformRuleFields ?? row?.PERMISSION
        };

        return h(FhFieldShow, {
          params,
          onCallbackList: (callback) => {
            if (callback?.businessId) {
              updateFieldPermission(tableList.value, entity.value, callback);
            }
          }
        });
      }
    };

    return colType;
  };

  const strLenCache = new Map();
  const MAX_CACHE_SIZE = 200;

  // 使用更严格的缓存策略，避免内存泄漏
  const getStrLen = (str) => {
    if (!str) return 0;
    if (strLenCache.has(str)) {
      return strLenCache.get(str);
    }
    const len = tools.getStrLen(str);
    strLenCache.set(str, len);
    if (strLenCache.size > MAX_CACHE_SIZE) {
      // 删除最旧的50项
      const keys = Array.from(strLenCache.keys()).slice(0, 50);
      keys.forEach((key) => strLenCache.delete(key));
    }
    return len;
  };

  // 获取子表单字段
  const getSubFormField = (fieldVos) => {
    return fieldVos?.map((v) => ({
      fieldKey: v.fieldKey,
      entityId: v.entityId,
      id: v.id,
      fieldType: v.fieldType
    }));
  };

  // 创建子表单列配置
  const createChildColumns = (item, tableList) => {
    const colTypeChildren = [];
    item?.modules?.forEach((el) => {
      const defaultCellWidth = [8, 9].includes(el.setting?.fieldType) ? 160 : getStrLen(el.setting?.fieldName) <= 6 ? 120 : getStrLen(el.setting?.fieldName) > 10 ? 220 : getStrLen(el.setting?.fieldName) * 20;
      const childRender = (row) => {
        const record = JSON.parse(JSON.stringify(row));
        const subFormData = record?.[item?.fieldKey] || [];

        if (subFormData.length) {
          return h('div', { class: 'child-table-container' }, [
            ...subFormData.map((subFormRecord) => {
              const params = {
                col: { ...el.setting },
                row: subFormRecord,
                fieldKey: el?.setting?.fieldKey,
                subFieldFullKey: `${item?.fieldKey}.${el?.setting?.fieldKey}`,
                mainField: el?.setting?.mainField,
                isRequired: isRequiredField(`${item?.fieldKey}.${el?.setting?.fieldKey}`, row?.PERMISSION, el?.setting?.isRequired),
                fieldValue: subFormRecord?.[el?.setting?.fieldKey],
                fieldType: el?.setting?.fieldType,
                fieldExt: el?.setting?.fieldExt,
                entity: entity.value,
                subEntityId: item.fieldExt,
                isSubForm: true,
                sourceType: 1,
                isAutoHeight: false,
                tableLevel: tableLevel,
                isAssTable: true, // 是否是查找关联表格
                permissions: subFormRecord.LOGIC_ROLE_PERMISSION,
                PERMISSION: row?.PERMISSION
              };
              console.log('表格产品路222', row);

              return h(FhFieldShow, {
                params,
                onCallbackList: (callback) => {
                  if (callback?.businessId) {
                    //替换成主数据id获取权限规则
                    callback.subFormFieldKey = item?.fieldKey;
                    callback.subId = callback.businessId;
                    callback.businessId = row.id;
                    updateFieldPermission(tableList.value, entity.value, callback);
                  }
                }
              });
            })
          ]);
        } else {
          return h('span', { class: 'table-cell-render-empty' }, '-');
        }
      };

      const getColType = {
        colKey: `${item.fieldKey}.${el?.setting?.fieldKey}`,
        title: el?.setting.fieldName,
        fieldName: el?.setting?.fieldName,
        fieldType: el?.setting?.fieldType,
        fieldExt: el?.setting?.fieldExt,
        width: defaultCellWidth,
        resize: { minWidth: [8, 9].includes(el?.setting?.fieldType) ? 160 : getStrLen(el?.setting?.fieldName) <= 5 ? 100 : 150, maxWidth: 500 },
        iconMore: false,
        isSubForm: true,
        align: getColumnAlign(el?.setting?.fieldType, el?.setting?.fieldExt),
        setting: item?.fieldVos?.length ? getSubFormField(item.fieldVos) : null,
        render: childRender
      };
      colTypeChildren.push(getColType);
    });
    return colTypeChildren;
  };

  //组装表格数据 表格数据 计算字段数据
  const assTableData = (tableData, computedInfoList) => {
    const resultData = [...tableData];
    resultData.forEach((itemA) => {
      const matchingItem = computedInfoList?.find((itemB) => itemB.businessId === itemA.id);
      if (matchingItem) {
        // 如果找到了匹配项，就把 computed 的值添加到 数据 中
        itemA.computedInfo = matchingItem;
      }
    });
    return resultData;
  };

  // 多层级表格渲染
  const getTableLevelColumns = (modulesList, tableList, businessData, subformRuleFields, subformFieldKey) => {
    console.log('业务规则参数33', subformRuleFields, subformFieldKey);
    const list = [];

    const tableEntity = businessData ? businessData : entity.value; //获取对象entity
    const getColumnsList = businessData ? modulesList.filter((item) => item.type == 20) : modulesList; //获取字段列表渲染表头

    getColumnsList?.forEach((item) => {
      const colConfig = createColumnConfig(item, tableEntity, tableList, subformRuleFields, subformFieldKey);
      if (item?.fieldType == 27 && tableLevel === 1) {
        // 子表单
        const childCols = createChildColumns(item, tableList);
        colConfig.children = childCols;
      }

      list.push(colConfig);
    });

    if (modulesList?.filter((v) => v.type == 40 && v?.value != (businessData < 200 ? objectRoleEnum[businessData] + ':add' : businessData + ':add'))?.length && tableLevel > 1) {
      list.push(createOperationColumn(modulesList, businessData));
    }

    console.log('表单columns', list);

    return list;
  };

  //获取计算状态
  const getComputedErrorLog = async (businessIdList, entityParams) => {
    try {
      const json = await setService.getComputedErrorLog({
        entity: entityParams,
        businessIdList: businessIdList
      });
      if (!json.error) {
        const result = json.result;
        return result;
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * 根据 ID 列表获取最新表格数据并合并
   * @param {Array} ids - 需要刷新的数据 ID 列表
   * @param {number|string} viewId - 视图 ID
   * @param {Array} currentTableData - 当前表格数据
   * @returns {Array} 合并后的表格数据
   */
  const getNewTableData = async (ids, viewId, currentTableData) => {
    if (!ids?.length) return currentTableData;
    try {
      const params = {
        entity: entity.value ?? entity,
        id: viewId,
        page: 1,
        pageSize: 200,
        recordIds: ids,
        apiIndex: `${entity.value ?? entity}-${Math.random()}`
      };
      const json = await tableService.getTableDataListPageV2(params);
      if (!json.error) {
        const recordsMap = new Map(json.result?.records?.map((r) => [r.id, r]) || []);
        return currentTableData?.map((item) => {
          const found = recordsMap.get(item.id);
          return found ? { ...item, ...found } : item;
        });
      }
    } catch (error) {
      console.error('获取表格数据失败', error);
    }
    return currentTableData;
  };

  return { getTableLevelColumns, getTableLevelEntity, getObjEntity, getComputedErrorLog, assTableData, getDetailKey, getNewTableData };
};

/**
 * 通用计算状态轮询 Hook
 * 传入一组 ID
 * 轮询获取计算状态
 * 返回计算中idMap、计算中id集合、已完成计算id集合、计算错误id集合
 * 通过回调通知结果
 * @param  options
 * @param  options.entity - 实体ID
 * @param  [options.tId=0] - 用于 API 请求去重的标识
 */
const useComputedStatusPollingCore = ({ entity, tId = 0, interval = 10000 }) => {
  const timer = ref(null);
  const pollingIds = ref([]); // 当前正在轮询的 ID 列表

  // 获取数据计算状态
  const fetchComputedStatus = async (businessIdList) => {
    if (!businessIdList?.length) return [];
    try {
      const json = await setService.getComputedErrorLog({
        entity: entity.value,
        businessIdList,
        apiIndex: `${tId}-${entity.value}-${Math.random()}`
      });
      return json.error ? [] : json.result || [];
    } catch (error) {
      console.error('获取计算状态失败', error);
      return [];
    }
  };

  // 解析计算状态结果 获取计算中idMap、计算中id集合、已完成计算id集合、计算错误id集合
  const parseComputedResult = (computedInfoList, queryIds) => {
    // 计算中id集合Map形式
    const computedMap = new Map(computedInfoList?.map((item) => [item.businessId, item]) || []);

    const stillComputingIds = []; // 仍在计算中的id集合
    const completedIds = []; // 已完成计算的id集合
    const errorIds = []; // 计算错误的id集合

    for (const id of queryIds) {
      const info = computedMap.get(id);
      if (!info || !computingStatus.includes(info.status)) {
        // 没有返回或状态不是计算中，说明已完成
        completedIds.push(id);
        if (info?.status === 2) {
          errorIds.push(id);
        }
      } else {
        stillComputingIds.push(id);
      }
    }

    return { computedMap, stillComputingIds, completedIds, errorIds };
  };

  /**
   * 通用轮询方法 - 可用于任意场景（详情页、表格等）
   * @param businessIdList - 需要轮询的业务ID列表
   * @param callbacks - 回调函数
   * @param  [callbacks.onStatusChange] - 状态变更回调
   * @param  [callbacks.onComplete] - 有数据完成计算时回调
   * @param  [callbacks.onAllComplete] - 所有数据完成计算时回调
   * @param  [callbacks.getPollingIds] - 动态获取需要轮询的ID（用于表格场景）
   */
  const pollByIds = async (businessIdList, callbacks = {}) => {
    const { onStatusChange, onComplete, onAllComplete, getPollingIds } = callbacks;

    // 清除之前的轮询
    clearTimer();

    // 初始化轮询ID
    pollingIds.value = [...businessIdList];

    // 没有需要轮询的数据
    if (!pollingIds.value.length) {
      onAllComplete?.();
      return;
    }

    const poll = async () => {
      try {
        // 支持动态获取需要轮询的ID（表格场景：从 tableData 中获取）
        const currentIds = getPollingIds ? getPollingIds() : pollingIds.value;

        // 所有数据都计算完成，停止轮询
        if (!currentIds.length) {
          clearTimer();
          onAllComplete?.();
          return;
        }

        const computedInfoList = await fetchComputedStatus(currentIds); // 获取计算状态 getLog
        const { computedMap, stillComputingIds, completedIds, errorIds } = parseComputedResult(computedInfoList, currentIds);

        // 更新轮询ID列表 处理中
        pollingIds.value = stillComputingIds;

        // 状态变更回调
        onStatusChange?.(computedMap, stillComputingIds);

        // 有数据完成计算 已完成
        if (completedIds.length) {
          onComplete?.(completedIds, errorIds);
        }

        // 检查是否所有数据都已完成
        if (!stillComputingIds.length) {
          clearTimer();
          onAllComplete?.();
        }
      } catch (error) {
        console.error('轮询发生错误', error);
        clearTimer();
      }
    };

    // 立即执行一次
    await poll();

    // 设置定时器
    if (timer.value === null && pollingIds.value.length) {
      timer.value = setInterval(poll, interval);
    }
  };

  /**
   * 轮询单个ID - 详情页场景
   * @param {string|number} businessId - 业务ID
   * @param {Object} callbacks - 回调函数
   * @param {Function} [callbacks.onStatusChange] - 状态变更回调 (computedInfo) => void
   * @param {Function} [callbacks.onComplete] - 完成回调 (hasError) => void
   */

  const pollById = async (businessId, callbacks = {}) => {
    const { onStatusChange, onComplete } = callbacks;

    await pollByIds([businessId], {
      onStatusChange: (computedMap) => {
        onStatusChange?.(computedMap.get(businessId) || null);
      },
      onComplete: (completedIds, errorIds) => {
        if (completedIds.includes(businessId)) {
          onComplete?.(errorIds.includes(businessId));
        }
      }
    });
  };

  /**
   * 手动刷新指定ID的计算状态（不启动轮询）
   * @param {string|number} businessId - 业务ID
   */
  const refreshStatus = async (businessIdList) => {
    if (!businessIdList?.length) return { computedMap: new Map(), completedIds: [], errorIds: [] };

    const computedInfoList = await fetchComputedStatus(businessIdList); // 获取计算状态
    return parseComputedResult(computedInfoList, businessIdList);
  };

  const clearTimer = () => {
    if (timer.value) {
      clearInterval(timer.value);
      timer.value = null;
    }
    pollingIds.value = [];
  };

  return {
    // 状态
    pollingIds,
    // 核心方法
    fetchComputedStatus,
    parseComputedResult,
    pollByIds,
    pollById,
    refreshStatus,
    clearTimer
  };
};

/**
 * 表格场景计算状态轮询 Hook（基于 useComputedStatusPollingCore 封装）
 */
const useComputedStatusPolling = ({ tableData, entity, viewId, delayGetSummary, tId = 0 }) => {
  // 内部管理的状态
  const allIds = ref([]); // 可选择的数据ID（排除计算中的数据）
  const errorRowList = ref([]); // 计算错误的数据ID

  // 使用核心轮询功能
  const { fetchComputedStatus, pollByIds, pollById, refreshStatus, clearTimer } = useComputedStatusPollingCore({
    entity,
    tId
  });

  // ==================== 表格场景特有方法 ====================

  // 根据id获取最新表格数据 getNewData
  const fetchTableData = async (ids) => {
    if (!ids?.length) return null;
    try {
      const params = {
        entity: entity.value,
        id: viewId.value,
        page: 1,
        pageSize: 200,
        recordIds: ids,
        apiIndex: `${tId}-${entity.value}-${Math.random()}`
      };
      const json = await tableService.getTableDataListPageV2(params);
      if (!json.error) {
        const recordsMap = new Map(json.result?.records?.map((r) => [r.id, r]) || []);
        return tableData.value?.map((item) => {
          const found = recordsMap.get(item.id);
          return found ? { ...item, ...found } : item;
        });
      }
    } catch (error) {
      console.error('获取表格数据失败', error);
    }
    return null;
  };

  // 更新表格数据的 computedInfo 组装数据
  const updateTableDataComputedInfo = (computedMap, targetIds = null) => {
    tableData.value = tableData.value.map((item) => {
      // 如果指定了目标ID，只更新这些ID的数据
      if (targetIds && !targetIds.includes(item.id)) {
        return item;
      }

      const newComputedInfo = computedMap.get(item.id) || null;
      // 只有计算中的数据才更新为最新状态，已完成的保持不变
      const shouldUpdate = targetIds || computingStatus.includes(item.computedInfo?.status);

      return {
        ...item,
        computedInfo: shouldUpdate ? newComputedInfo : item.computedInfo
      };
    });
  };

  // 根据 tableData 同步 allIds 和 errorRowList
  const syncStatusFromTableData = () => {
    const selectableIds = [];
    const errorIds = [];

    for (const item of tableData.value) {
      const status = item?.computedInfo?.status;
      if (computingStatus.includes(status)) {
        continue;
      }
      selectableIds.push(item.id);
      if (status === 2) {
        errorIds.push(item.id);
      }
    }

    allIds.value = selectableIds;
    errorRowList.value = errorIds;
  };

  // 从 tableData 中获取计算中的数据ID
  const getComputingIdsFromTableData = () => {
    return tableData.value.filter((item) => computingStatus.includes(item?.computedInfo?.status)).map((item) => item.id);
  };

  // ==================== 对外暴露的方法 ====================

  // 开始轮询（表格场景）
  const startPolling = async () => {
    // 初始化状态
    syncStatusFromTableData();

    const computingIds = getComputingIdsFromTableData();
    if (!computingIds.length) return;

    await pollByIds(computingIds, {
      // 动态获取需要轮询的ID
      getPollingIds: getComputingIdsFromTableData,
      // 状态变更时更新表格数据
      onStatusChange: (computedMap) => {
        updateTableDataComputedInfo(computedMap);
        syncStatusFromTableData();
      },
      // 有数据完成计算时刷新数据
      onComplete: async (completedIds) => {
        const newItems = await fetchTableData(completedIds);
        if (newItems) {
          tableData.value = newItems;
          delayGetSummary?.();
        }
      }
    });
  };

  // 手动刷新指定数据的计算状态
  const refreshComputedStatus = async (businessIdList) => {
    if (!businessIdList?.length) return;

    const { computedMap, completedIds } = await refreshStatus(businessIdList);
    updateTableDataComputedInfo(computedMap, businessIdList);
    syncStatusFromTableData();

    if (completedIds.length) {
      const newItems = await fetchTableData(completedIds);
      if (newItems) {
        tableData.value = newItems;
        delayGetSummary?.();
      }
    }
  };

  return {
    // 状态
    allIds,
    errorRowList,
    // 表格场景方法
    startPolling,
    refreshComputedStatus,
    clearTimer,
    // 通用方法（可用于详情页等场景）
    pollByIds,
    pollById,
    refreshStatus,
    fetchComputedStatus
  };
};

export { useLyTableConfig, useComputedStatusPolling, useComputedStatusPollingCore };
